// ─────────────────────────────────────────────────────────────────────────
//  ClickViewer · web · 화면 틀
//
//  ★★ 머리글에는 **읽기 전용 정보만** 둔다. 동작은 화면 아래 1/3 이다.
//     한 손에 도면이나 무전기를 든 사람은 화면 위쪽에 엄지가 안 닿는다.
//
//  ⚠️ 옛 머리글은 오른쪽 끝에 **로그아웃 하나만** 있었다. 즉 폰에서 뻗은 엄지가 닿는
//     유일한 상단 버튼이 **세션을 끊는 버튼**이었다. 잘못 누르면 8자리 코드 로그인을
//     처음부터 다시 해야 하고, 코드는 60초 쿨다운이라 **되돌릴 수 없다.**
//     → 로그아웃을 「내 계정」 메뉴 **안**으로 옮겼다. 두 번 눌러야 나간다.
//
//  ★ 하단 고정 바(`bottomBar`)는 폰에서만 붙는다. 그 판정은 **JS 가 아니라 CSS** 다 —
//    global.css 의 `.cv-bottombar` 미디어쿼리가 `.cv-form-grid` 접힘과 같은 지점에서
//    같이 켜진다. 자세한 이유는 breakpoint.ts 머리말에.
//
//  ⚠️ `./ColorScheme` import 는 **모듈 부작용**이 있다(기본을 light 로 못박는다).
//     지우지 마라 — 지우면 여름 저녁 19:30 에 현장 폰이 갑자기 어두워진다.
// ─────────────────────────────────────────────────────────────────────────

import { Box, Button, Container, Group, Menu, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { USE_MOCK } from '../config';
import { A } from '../router';
import { useSession } from '../auth/SessionProvider';
import { useThemeColorMeta } from './ColorScheme';

/**
 * 가짜 데이터 경고 띠.
 *
 * ⚠️ 눈에 아프게 만든 것은 의도다. mock 을 켜 놓은 것을 잊고 "잘 되네" 하는 것이
 *    이 프로젝트에서 제일 위험한 착각이다 — mock 에는 쿠키도 RLS 도 속도 제한도 없다.
 */
function MockBanner() {
  if (!USE_MOCK) return null;
  return (
    <Box
      role="status"
      style={{
        background: 'var(--cv-warn-bg)',
        color: 'var(--cv-warn)',
        borderBottom: '2px solid var(--cv-warn)',
        textAlign: 'center',
        padding: '8px 12px',
        fontWeight: 700,
        // 14px. 12px 로 두면 야외에서 이 경고 자체가 안 보인다 — 경고의 뜻이 없어진다.
        fontSize: 14,
      }}
    >
      가짜 데이터입니다 (VITE_MOCK=1) — 서버에 아무것도 묻지 않고 있습니다
    </Box>
  );
}

/**
 * 「내 계정」 메뉴.
 *
 * ★ 메뉴를 열면 **지금 로그인한 주소가 16px 고정폭 ink 로** 나온다.
 *   이게 이 메뉴의 진짜 목적이다 — 이 제품에서 사람이 막히는 가장 흔한 원인이
 *   「어느 주소로 로그인했는지 모른다」이고, 그 대조를 할 수 있는 곳이 여기뿐이다.
 *   (초대는 A 주소로 왔는데 B 주소로 로그인한 상황이 실제로 흔하다.)
 */
function AccountMenu({ email, onSignOut }: { email: string; onSignOut: () => void }) {
  return (
    <Menu position="bottom-end" withinPortal shadow="none" width={280}>
      <Menu.Target>
        <Button variant="default" size="md" px="sm">
          내 계정
        </Button>
      </Menu.Target>

      <Menu.Dropdown style={{ border: '1px solid var(--cv-line-strong)' }}>
        <Box px="md" py="sm">
          <Text size="sm" c="var(--cv-muted)">
            지금 로그인한 주소
          </Text>
          {/* ★ 16px · ink · 고정폭. muted 로 그리면 대조가 불가능해진다(야외 2.86:1).
              ⚠️ 자르지 마라. 자른 주소로는 오타를 못 찾는다. 길면 줄이 바뀌는 게 낫다. */}
          <Text size="md" fw={600} className="cv-mono" style={{ wordBreak: 'break-all' }}>
            {email}
          </Text>
        </Box>

        <Menu.Divider />

        {/* ⚠️ 「되돌릴 수 없다」를 항목 안에 적어 둔다. 로그아웃하면 코드를 다시 받아야
            하는데 그건 60초 쿨다운이라 즉시 되돌릴 수 없다.

            ⚠️ `color="danger"` 만 주면 Mantine 이 램프의 **6번**(#C13A2C · 흰 위 5.37)을
               고른다. AA 는 넘지만 우리가 문서에 적어 둔 위험색은 **7번**(#A32218 · 7.50)이다.
               색이 문서와 화면에서 갈리면 다음 사람이 어느 쪽을 믿을지 알 수 없으므로
               `color` 는 hover 배경용으로만 두고 글자색은 토큰으로 못박는다. [실측 2026-08-06] */}
        <Menu.Item
          color="danger"
          onClick={onSignOut}
          style={{ minHeight: 48, color: 'var(--cv-danger)' }}
        >
          <Text size="md" fw={600}>
            로그아웃
          </Text>
          <Text size="sm" c="var(--cv-ink-2)">
            다시 들어오려면 코드를 새로 받아야 합니다
          </Text>
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

export function Shell({
  children,
  title,
  /**
   * 폰에서 화면 아래에 고정될 동작 묶음. 주동작·파괴적 동작은 **여기** 둔다.
   * 데스크톱에서는 본문 흐름 그대로 맨 아래에 놓인다(고정 안 됨).
   */
  bottomBar,
}: {
  children: ReactNode;
  title?: ReactNode;
  bottomBar?: ReactNode;
}) {
  const { state, signOut } = useSession();
  useThemeColorMeta();

  return (
    <>
      <MockBanner />

      <Box
        component="header"
        style={{
          borderBottom: '1px solid var(--cv-line-strong)',
          background: 'var(--cv-surface)',
        }}
      >
        <Container size="lg" py="sm">
          <Group justify="space-between" wrap="nowrap" gap="sm">
            {/* 홈 링크. `header a` 규칙이 44px 을 준다(global.css §②).
                ⚠️ 옛 코드에서 이 링크는 세로 **28.8px** 이었다 — 규칙이
                `.mantine-Button-root` 계열만 잡아서 맨 `<a>` 가 빠져 있었다. */}
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
              <A href="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                {/* ★ 모든 화면 맨 위에 뜨는 이름이다. **ClickView** [2026-08-12].
                    ⚠️ 저장소·Worker·버킷·DB 는 `clickviewer` 그대로다 — 여기와 섞지 마라.
                       그물: `apps/web/src/brandContract.spec.ts` */}
                <Text fw={700} size="lg" style={{ letterSpacing: '-0.02em' }}>
                  ClickView
                </Text>
              </A>
              {title ? (
                // 지금 어느 현장인지 = 데이터다. muted 금지, ink-2.
                <Text c="var(--cv-ink-2)" size="sm" truncate>
                  {title}
                </Text>
              ) : null}
            </Group>

            {state.status === 'authed' ? (
              <AccountMenu email={state.user.email} onSignOut={() => void signOut()} />
            ) : null}
          </Group>
        </Container>
      </Box>

      <Container size="lg" py="lg" component="main">
        {children}
      </Container>

      {bottomBar ? (
        <Container size="lg" px="md" pb={0}>
          <div className="cv-bottombar">{bottomBar}</div>
        </Container>
      ) : null}
    </>
  );
}

/**
 * 머리글 없이 가운데에 놓는 틀. 로그인 · 404 · 연결 실패 · 초대 안내가 쓴다.
 *
 * ★ 폭을 `520` 으로 잡았다. 로그인 카드가 이보다 넓으면 8자리 코드칸이 화면 가운데에
 *   덩그러니 떠서 「몇 자리인지」가 눈에 안 들어온다.
 */
export function CenteredShell({ children }: { children: ReactNode }) {
  useThemeColorMeta();
  return (
    <>
      <MockBanner />
      <Container size={520} py="xl" component="main">
        {children}
      </Container>
    </>
  );
}
